import type {
  AdminUsersRequest,
  CreateUserRequest,
  FormattedUser,
  FormattedUsers,
  UpdateUserRequest,
  UserResponse,
  UsersResponse
} from '@library/types/user';
import { transformUser, transformUsers } from '@bo-utils/user';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';

const { admin } = apiPaths;

export const userApi = api.injectEndpoints({
  endpoints: (build) => ({
    // Note: The raw responses from these first two endpoints return user objects with inconsistent
    // typing for ID fields; the `roles` array within the top-level `attributes` for each user has
    // IDs of type number while everywhere else IDs are of type string. transformUser + "Raw" types
    // handle this as a band-aid while the backend team looks into the issue
    getAdminUsers: build.query<FormattedUsers | undefined, AdminUsersRequest>({
      query: ({
        page = 1,
        perPage = 25,
        role_ids = [],
        search = undefined,
        sort = undefined,
        vip_host = undefined
      }: AdminUsersRequest) => ({
        url: `${admin}admin_users`,
        method: 'GET',
        params: {
          page,
          per_page: perPage,
          role_ids: role_ids?.join(','),
          search,
          sort,
          vip_host
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: UsersResponse) => transformUsers(response),
      providesTags: ['User']
    }),
    getAdminUser: build.query<FormattedUser | undefined, { id: string }>({
      query: ({ id }) => ({
        url: `${admin}admin_users/${id}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: UserResponse) =>
        transformUser(response.data),
      providesTags: ['User']
    }),
    createAdminUser: build.mutation<UserResponse, CreateUserRequest>({
      query: (payload) => ({
        url: `${admin}admin_users`,
        method: 'POST',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      invalidatesTags: ['User']
    }),
    updateAdminUser: build.mutation<
      UserResponse,
      { id: string } & UpdateUserRequest
    >({
      query: ({ id, ...payload }) => ({
        url: `${admin}admin_users/${id}`,
        method: 'PATCH',
        body: payload,
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      })
    })
  })
});

export const {
  useGetAdminUsersQuery,
  useGetAdminUserQuery,
  useCreateAdminUserMutation,
  useUpdateAdminUserMutation
} = userApi;
